import { FC, useState } from 'react'
import { Modal, Button, Form, Alert } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import modalStyles from '../../styles/modals.module.css'
import adminStyles from '../../pages/competitions/admin/AdminForms.module.css'
import DateTimePicker from '../ui/DateTimePicker'
import SegmentedSelector, { type SegmentedOption } from '../ui/SegmentedSelector/SegmentedSelector'
import { tenantService } from '../../services/tenantService'
import type { WeatherStatus, WeatherStatusDTO } from '../../types/dtos'
import { CloudLightning, CloudRainHeavy, CloudSun } from 'react-bootstrap-icons'

interface WeatherWarningModalProps {
  show: boolean
  onHide: () => void
  tenantId: string
  currentStatus: WeatherStatusDTO | null
  onSuccess: (dto: WeatherStatusDTO) => void
}

const WeatherWarningModal: FC<WeatherWarningModalProps> = ({
  show,
  onHide,
  tenantId,
  currentStatus,
  onSuccess,
}) => {
  const { t } = useTranslation('common')

  const [stage, setStage] = useState<WeatherStatus>(currentStatus?.status ?? 'NONE')
  const [reason, setReason] = useState(currentStatus?.reason ?? '')
  const [estimatedResumeAt, setEstimatedResumeAt] = useState(currentStatus?.estimatedResumeAt ?? '')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset form when modal opens
  const [prevShow, setPrevShow] = useState(show)
  if (show !== prevShow) {
    setPrevShow(show)
    if (show) {
      setStage(currentStatus?.status ?? 'NONE')
      setReason(currentStatus?.reason ?? '')
      setEstimatedResumeAt(currentStatus?.estimatedResumeAt ?? '')
      setError(null)
    }
  }

  const needsReason = stage !== 'NONE'
  const isReasonEmpty = needsReason && !reason.trim()
  const canSubmit = !isReasonEmpty && !isSubmitting

  const stageOptions: SegmentedOption<WeatherStatus>[] = [
    { value: 'NONE', label: t('weather.modal.stage_none'), icon: CloudSun },
    { value: 'WARNING', label: t('weather.modal.stage_warning'), icon: CloudRainHeavy },
    { value: 'DELAYED', label: t('weather.modal.stage_delayed'), icon: CloudLightning },
  ]

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return
    setIsSubmitting(true)
    setError(null)
    try {
      const body = {
        status: stage,
        reason: needsReason ? reason.trim() : undefined,
        estimatedResumeAt: stage === 'DELAYED' && estimatedResumeAt ? estimatedResumeAt : undefined,
      }
      const dto = await tenantService.setWeatherStatus(tenantId, body)
      onSuccess(dto)
      onHide()
    } catch (err) {
      const e = err as { message?: string }
      setError(e.message || t('weather.modal.reason_required'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className={modalStyles.xenGolfModal}
      contentClassName={modalStyles.xenGolfModalContent}
    >
      <Modal.Header closeButton className={modalStyles.xenGolfModalHeader}>
        <Modal.Title className={modalStyles.xenGolfModalTitle}>
          {t('weather.modal.title')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={`${modalStyles.xenGolfModalBody} ${adminStyles.editForm}`}>
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

        <Form.Group className="mb-3">
          <Form.Label id="weatherStageLabel">{t('weather.modal.stage_label')}</Form.Label>
          <SegmentedSelector<WeatherStatus>
            options={stageOptions}
            value={stage}
            onChange={setStage}
            disabled={isSubmitting}
            ariaLabelledBy="weatherStageLabel"
          />
        </Form.Group>

        {needsReason && (
          <Form.Group className="mb-3">
            <Form.Label htmlFor="weather-reason" className="required">
              {t('weather.modal.reason_label')}
            </Form.Label>
            <Form.Control
              as="textarea"
              id="weather-reason"
              rows={3}
              maxLength={255}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              isInvalid={isReasonEmpty && reason !== ''}
              disabled={isSubmitting}
              placeholder={t('weather.modal.reason_placeholder')}
            />
          </Form.Group>
        )}

        {stage === 'DELAYED' && (
          <Form.Group>
            <Form.Label htmlFor="weather-eta">
              {t('weather.modal.eta_label')}
            </Form.Label>
            <DateTimePicker
              id="weather-eta"
              value={estimatedResumeAt}
              onChange={(value) => setEstimatedResumeAt(value)}
              disabled={isSubmitting}
            />
            <Form.Text className="text-muted">
              {t('weather.modal.eta_hint')}
            </Form.Text>
          </Form.Group>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="warning" size="sm" onClick={onHide} disabled={isSubmitting}>
          {t('weather.modal.cancel_button')}
        </Button>
        <Button
          variant="success"
          size="sm"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {t('weather.modal.save_button')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default WeatherWarningModal
